import styled, { css } from 'styled-components';
import { down } from 'styled-breakpoints';
import { Link } from 'react-router-dom';

import { TransientProps } from '@common/types';

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.875rem;
`;

export const HeaderLeft = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    color: ${theme.colors.brand.primary.default};

    > h2 {
      font-size: 1rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
    }
  `}
`;

export const CountBadge = styled.span`
  ${({ theme }) => css`
    font-size: 0.625rem;
    font-weight: 600;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    padding: 0.1875rem 0.5rem;
    border-radius: 1.25rem;
  `}
`;

export const ShowAllLink = styled(Link)`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.brand.secondary.default};
    font-weight: 600;
  `}
`;

export const CarouselWrapper = styled.div`
  position: relative;
`;

export const ScrollContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0.5rem 0 1.5rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

type ArrowProps = TransientProps<{ direction: 'left' | 'right' }>;

export const Arrow = styled.button<ArrowProps>`
  ${({ theme, $direction }) => css`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${$direction === 'left' ? 'left: -0.5rem' : 'right: -0.5rem'};
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    color: ${theme.colors.foreground[100]};

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}
`;

const gradients = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(160deg, #f093fb, #f5576c)',
  'linear-gradient(185deg, #4facfe, #00f2fe)',
  'linear-gradient(210deg, #43e97b, #38f9d7)',
  'linear-gradient(235deg, #fa709a, #fee140)',
];

export const VideoCard = styled.div<{ $index: number }>`
  ${({ theme }) => css`
    width: calc((100% - 2rem) / 3);
    min-width: 13.75rem;
    background: ${theme.colors.background[100]};
    border-radius: 0.875rem;
    overflow: hidden;
    border: 1px solid ${theme.colors.background[40]};
    flex-shrink: 0;
    scroll-snap-align: start;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
    }

    &:hover .play-overlay {
      opacity: 1;
    }

    > a {
      text-decoration: none;
      color: inherit;
    }

    ${down('md')} {
      min-width: 16rem;
    }
  `}
`;

export const VideoThumb = styled.div<{ $index: number }>`
  ${({ $index }) => css`
    height: 10.3125rem;
    position: relative;
    background: ${gradients[$index % gradients.length]};
    display: flex;
    align-items: center;
    justify-content: center;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: absolute;
      inset: 0;
    }
  `}
`;

export const DurationBadge = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  padding: 0.125rem 0.4375rem;
  border-radius: 0.375rem;
  font-size: 0.625rem;
  font-weight: 600;
  background: rgba(0, 0, 0, 0.65);
  color: white;
  z-index: 1;
`;

export const PlayOverlay = styled.div`
  ${({ theme }) => css`
    opacity: 0;
    transition: opacity 0.3s ease;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    z-index: 1;
    color: ${theme.colors.brand.secondary.default};
  `}
`;

export const VideoInfo = styled.div`
  padding: 0.75rem 0.875rem;

  > h3 {
    font-size: 0.8125rem;
    font-weight: 600;
    margin-bottom: 0.125rem;
  }

  > p {
    font-size: 0.6875rem;
    color: var(--colors-foreground-40);
  }
`;
